import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../data";
import { toast } from "./Toast";
import { asDateFormat, currentLang, formatDay, t as uiT, type DateFormat, type Lang } from "../lib/sprache";

/** Wahl im Formular: leer (nach der Sprache) oder eines der festen Formate. */
export type DateFormatChoice = DateFormat | "";

/** Beschriftung je Wahl mit Beispiel (heutiges Datum), in der Oberflächensprache. */
export function dateFormatOptions(lang: Lang, example: string, t: (de: string, en: string) => string = uiT): { value: DateFormatChoice; label: string }[] {
  const [y = "2026", m = "09", d = "24"] = example.split("-");
  const sample = (f: DateFormat | null) => formatDay(y, m, d, lang, f);
  return [
    { value: "", label: `${t("Wie die Sprache", "Same as the language")} (${sample(null)})` },
    { value: "system", label: `${t("Wie dieses Gerät", "Same as this device")} (${sample("system")})` },
    // englische Oberfläche: das Muster allein (ein Beispiel mit Punkten läse sich wie ein deutscher Rest)
    { value: "dmy", label: lang === "en" ? t("TT.MM.JJJJ", "DD.MM.YYYY") : `${t("TT.MM.JJJJ", "DD.MM.YYYY")} (${sample("dmy")})` },
    { value: "ymd", label: `${t("JJJJ-MM-TT", "YYYY-MM-DD")} (${sample("ymd")})` },
    { value: "mdy", label: `${t("MM/TT/JJJJ", "MM/DD/YYYY")} (${sample("mdy")})` },
  ];
}

/** Speichert das eigene Datumsformat (user_preferences.date_format, 0098); leer = nach der Sprache. */
export async function saveDateFormat(userId: string, choice: DateFormatChoice): Promise<void> {
  const value = asDateFormat(choice);
  const own = await api.select<{ user_id: string }>("user_preferences", { columns: "user_id", filters: [["user_id", "eq", userId]] });
  if (own.length) await api.update("user_preferences", [["user_id", "eq", userId]], { date_format: value });
  else await api.insert("user_preferences", { user_id: userId, date_format: value });
}

/**
 * Eigenes Datumsformat (PARITAET-R3-7, wie „Datumsformat“ in den Einstellungen des bisherigen Portals): gilt für diese Person
 * in beiden Portalen und auf jedem Gerät. Belege, Verträge und Mails behalten ihr eigenes Format.
 */
export function DatumsformatWahl({ userId, value, today, t = uiT, heading = "h2" }: {
  userId: string;
  value: string | null | undefined;
  /** heutiger Kalendertag 'YYYY-MM-DD' für das Beispiel */
  today: string;
  t?: (de: string, en: string) => string;
  heading?: "h2" | "h3";
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const current: DateFormatChoice = asDateFormat(value) ?? "";
  const H = heading;
  async function choose(next: DateFormatChoice) {
    if (next === current) return;
    setBusy(true);
    try {
      await saveDateFormat(userId, next);
      // Rahmen liest das Format aus dem Konto und zeichnet den Inhalt neu
      await qc.invalidateQueries({ queryKey: ["account"] });
      toast(t("Datumsformat gespeichert.", "Date format saved."));
    } catch (e) {
      toast((e as Error).message, "alert");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel stack" aria-labelledby="konto-datumsformat">
      <H id="konto-datumsformat">{t("Datumsformat", "Date format")}</H>
      <label>
        {t("So zeigt das Portal Daten an", "How the portal shows dates")}
        <select value={current} disabled={busy} onChange={(e) => void choose(e.target.value as DateFormatChoice)} aria-describedby="datumsformat-hinweis">
          {dateFormatOptions(currentLang(), today, t).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      <small id="datumsformat-hinweis">{t("Gilt nur für deine Ansicht, auf jedem Gerät. Rechnungen, Verträge und Mails behalten ihr eigenes Format.", "Applies only to your view, on every device. Invoices, contracts and emails keep their own format.")}</small>
    </section>
  );
}
